<template>
  <div>
    <h1>Grants</h1>

    <b-button v-b-modal.modal-crud variant="primary" style="margin-bottom:20px;">New Grant</b-button>
    <b-modal id="modal-crud" hide-header hide-footer>
      <h2>{{formTitle}}</h2>
      <hr/>
      <form ref="form" @submit.prevent="save">

          <b-form-group label="Grantee" label-for="grantee-input">
            <b-form-select id="grantee-input"
                v-model="$v.editedItem.granteeId.$model"  
                :state="$v.editedItem.granteeId.$dirty ? !$v.editedItem.granteeId.$error : null"
                  aria-describedby="input-grantee-live-feedback"              
                :options="grantees"
                value-field="id"
                text-field="name"
                aria-label="Select Grantee">
                <template slot="first">
                    <option :value="null" disabled>Select Grantee</option>
                </template>
            </b-form-select>
            <b-form-invalid-feedback id="input-grantee-live-feedback">
              This is a required field.
            </b-form-invalid-feedback> 
            <b-form-text id="grantee-input-help">Required.</b-form-text> 
          </b-form-group>

          <b-form-group label="Funding Source" label-for="funding-input">
            <b-form-select id="funding-input"
                v-model="$v.editedItem.grantCode.$model"  
                :state="$v.editedItem.grantCode.$dirty ? !$v.editedItem.grantCode.$error : null"
                  aria-describedby="input-funding-live-feedback"              
                :options="fundingSources"
                value-field="code"
                text-field="label"
                aria-label="Select Funding Source">
                <template slot="first">
                    <option :value="null" disabled>Select Funding Source</option>
                </template>
            </b-form-select>
            <b-form-invalid-feedback id="input-funding-live-feedback">
              This is a required field.
            </b-form-invalid-feedback> 
            <b-form-text id="funding-input-help">Required.</b-form-text> 
          </b-form-group>

          <b-form-group 
            label="Start Date" label-for="start-date-input" 
            invalid-feedback="Start Date is required.">
            <date-picker id="start-date-input" v-model="editedItem.startDate" required :config="options"></date-picker>
            <b-form-text id="start-date-input-help">Required. Format: YYYY-MM-DD.</b-form-text>
          </b-form-group>
          <b-form-group 
            label="End Date" label-for="end-date-input">
            <date-picker id="end-date-input" v-model="editedItem.endDate" :config="options"></date-picker>
            <b-form-text id="end-date-input-help">Format: YYYY-MM-DD.</b-form-text>  
          </b-form-group>

          <b-button @click="close" style="margin-right:15px;">Cancel</b-button>
          <b-button type="submit" variant="primary" :disabled="$v.editedItem.$invalid">Save</b-button>
        </form>
    </b-modal>

    <b-form inline style="margin-bottom:15px;">
      <label class="search-label">Grant:</label>
      <b-form-select id="grant-input"
          v-model="search"                
          :options="grantNames"
          style="width:25%;"
          aria-label="Select Grant">
          <template slot="first">
            <option :value="null">Select Grant</option>
          </template>
      </b-form-select>
      
      <b-button @click="fetchGrants()" variant="primary" style="margin-left:5px;">Search</b-button>
    </b-form>

    <div>
      <b-table hover small responsive show-empty
        :fields="fields"
        :items="grants"
        :busy="isBusy">
        <template v-slot:cell(actions)="row">
          <b-button size="sm" @click="editItem(row.item)" class="mr-1">
            edit
          </b-button>
        </template>         
        <template v-slot:no-data>
          <v-btn color="primary" @click="initialize">Reset</v-btn>
        </template>

        <template v-slot:empty="scope">
          <p>No data found, please update search criteria.</p>
        </template>

        <div slot="table-busy" class="text-center text-danger my-2">
          <b-spinner class="align-middle" style="margin-right:5px; margin-top:12px;"></b-spinner>
          <strong>Loading, Please Wait...</strong>
        </div>  
      </b-table>
    </div>
  </div>
</template>

<script>
  import axios from 'axios';
  import axiosRetry from 'axios-retry';
  import { validationMixin } from 'vuelidate'
  import { required } from 'vuelidate/lib/validators'
  import datePicker from 'vue-bootstrap-datetimepicker';
  import 'pc-bootstrap4-datetimepicker/build/css/bootstrap-datetimepicker.css';

  axiosRetry(axios, { retries: 3,
                      retryDelay: axiosRetry.exponentialDelay });

  export default {
    mixins: [validationMixin],
    title:  'Grants',
    data: () => ({
      isBusy: false,
      fields: [
        {key: 'uuid', sortable: false, label: 'Grant ID'},
        {key: 'granteeName', sortable: true, label: 'Grantee Name'},
        {key: 'grantName', sortable: true, label: 'Grant Type'},
        {key: 'startDate', sortable: false, label: 'Start Date'},
        {key: 'endDate', sortable: false, label: 'End Date'},
        {key: 'actions', label: 'Actions'}
      ],
      search: '',
      grants: [],
      grantees: [],
      grantNames: [],
      fundingSources: [],
      editedIndex: -1,
      editedItem: {
        id: null,
        uuid: null,
        granteeName: "",
        granteeId: 0,
        grantCode: "",
        grantName: "",
        startDate: null,
        endDate: null
      },
      defaultItem: {
        id: null,
        uuid: null,
        granteeName: "",
        granteeId: 0,
        grantCode: "",
        grantName: "",
        startDate: null,
        endDate: null
      },
      options: {
          format: 'YYYY-MM-DD',
          useCurrent: false,
      }    
    }),

    validations: {
      editedItem: {
        granteeId: {
          required
        },
        grantCode: {
          required
        },
        startDate: {
          required,
          minValue: true
        }
      }
    },

    components: {
     datePicker
    },

    computed: {
      formTitle () {
        return this.editedIndex === -1 ? 'New Grant' : 'Edit Grant'
      }
    },

    watch: {
      dialog (val) {
        val || this.close()
      }
    },

    created () {
      this.initialize()
    },

    methods: {
      fetchGrants() {
          
            this.isBusy = true
            axios.get(`${process.env.VUE_APP_API_BASE_URL}/admin/grants/${this.search}`)
              .then(response => {
                  this.grants = response.data
                  this.isBusy = false
              })
          
      },

      fetchGrantNames() {
          this.isLoading = true;
          axios.get(`${process.env.VUE_APP_API_BASE_URL}/admin/grant/names`)
            .then(response => {
                this.grantNames = response.data;
                this.isLoading = false;
            })
      },

      fetchGrantees() {
          axios.get(`${process.env.VUE_APP_API_BASE_URL}/admin/grantees`)
            .then(response => {
                this.grantees = response.data
            })
      },

      fetchFundingSources() {
          axios.get(`${process.env.VUE_APP_API_BASE_URL}/admin/values/grant`)
            .then(response => {
                this.fundingSources = response.data
            })
      },

      initialize () {
          this.isBusy = true          
          this.fetchGrantees()
          this.fetchGrantNames()
          this.fetchFundingSources()
          this.isBusy = false
      },

      editItem (item) {
        this.editedIndex = this.grants.indexOf(item)
        this.editedItem = Object.assign({}, item)
        this.$bvModal.show('modal-crud')
      },

      deleteItem (item) {
        const index = this.grants.indexOf(item)
        confirm('Are you sure you want to delete this item?') && this.grants.splice(index, 1)
      },

      close () {
        this.$bvModal.hide('modal-crud')
        setTimeout(() => {
          this.editedItem = Object.assign({}, this.defaultItem)
          this.editedIndex = -1
        }, 300)
      },

      save () {
        if (this.editedIndex > -1) {
            axios.post(`${process.env.VUE_APP_API_BASE_URL}/admin/grants`, this.editedItem)
            .then(response => { 
                Object.assign(this.grants[this.editedIndex], response.data)
                this.$bvToast.toast('Grant Saved', {
                  title: 'Status',
                  toaster: 'b-toaster-top-center',
                  solid: true,
                  variant: 'success',
                  appendToast: true
                })
                this.fetchGrants()
            });
          
        } else {
            axios.post(`${process.env.VUE_APP_API_BASE_URL}/admin/grants`, this.editedItem)
            .then(response => { 
                this.grants.push(response.data)
                this.$bvToast.toast('Grant Saved', {
                  title: 'Status',
                  toaster: 'b-toaster-top-center',
                  solid: true,
                  variant: 'success',
                  appendToast: true
                })
                this.fetchGrants()
            });
          
        }
        this.close()
      },

      formatDate (date) {
        if (!date) return null

        const [year, month, day] = date.split('-')
        return `${month}/${day}/${year}`
      },

      parseDate (date) {
        if (!date) return null

        const [month, day, year] = date.split('/')
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
      }
    }
  }
</script>